import { ReactNode } from "react";
import { Routes, Route, Navigate, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "./auth";
import { Role } from "./api";
import Login from "./pages/Login";
import Home from "./pages/Home";
import ReadNewsletter from "./pages/ReadNewsletter";
import ArticlesList from "./pages/ArticlesList";
import ArticleEdit from "./pages/ArticleEdit";
import NewslettersList from "./pages/NewslettersList";
import NewsletterEdit from "./pages/NewsletterEdit";
import AdminUsers from "./pages/AdminUsers";

function Loading() {
  return (
    <div className="mono" style={{ padding: 80, textAlign: "center", color: "var(--muted)", fontSize: 12 }}>
      Loading…
    </div>
  );
}

function RequireAuth({ children, min }: { children: ReactNode; min?: Role }) {
  const { me, loading, can } = useAuth();
  const location = useLocation();
  if (loading) return <Loading />;
  if (!me) return <Navigate to={`/login?returnTo=${encodeURIComponent(location.pathname)}`} replace />;
  if (min && !can(min)) return <Forbidden />;
  return <>{children}</>;
}

function Forbidden() {
  return (
    <div className="page-wrap" style={{ textAlign: "center" }}>
      <h2 style={{ fontFamily: "'Cinzel',serif" }}>Access denied</h2>
      <p style={{ color: "var(--muted)" }}>Your role does not permit this area. Ask an admin for access.</p>
      <NavLink to="/" className="btn">
        ← Back
      </NavLink>
    </div>
  );
}

function AppBar() {
  const { me, logout, can } = useAuth();
  if (!me) return null;
  return (
    <div className="appbar no-print">
      <div className="appbar-inner">
        <NavLink to="/" style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img src="/eagle.png" alt="" style={{ height: 22, filter: "var(--logo-filter)", opacity: 0.9 }} />
          <span style={{ fontFamily: "'Cinzel',serif", fontWeight: 700, fontSize: 14, letterSpacing: ".06em", color: "var(--ink)" }}>
            VOX IMPERIALIS
          </span>
        </NavLink>
        <nav style={{ display: "flex", gap: 18, marginLeft: 20 }}>
          <NavLink to="/" end className="navlink">
            Issues
          </NavLink>
          {can("contributor") && (
            <NavLink to="/articles" className="navlink">
              Articles
            </NavLink>
          )}
          {can("editor") && (
            <NavLink to="/newsletters" className="navlink">
              Newsletters
            </NavLink>
          )}
          {can("admin") && (
            <NavLink to="/admin/users" className="navlink">
              Members
            </NavLink>
          )}
        </nav>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }}>
          <span className="mono" style={{ fontSize: 10.5, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".08em" }}>
            {me.name} · {me.role}
          </span>
          <button className="btn" onClick={logout}>
            Log out
          </button>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <div className="vox-desk" style={{ minHeight: "100vh", background: "var(--desk)" }}>
      <AppBar />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<RequireAuth><Home /></RequireAuth>} />
        <Route path="/read/:slug" element={<RequireAuth><ReadNewsletter /></RequireAuth>} />
        <Route path="/articles" element={<RequireAuth min="contributor"><ArticlesList /></RequireAuth>} />
        <Route path="/articles/:id" element={<RequireAuth min="contributor"><ArticleEdit /></RequireAuth>} />
        <Route path="/newsletters" element={<RequireAuth min="editor"><NewslettersList /></RequireAuth>} />
        <Route path="/newsletters/:id" element={<RequireAuth min="editor"><NewsletterEdit /></RequireAuth>} />
        <Route path="/admin/users" element={<RequireAuth min="admin"><AdminUsers /></RequireAuth>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}
